import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add as AddIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import InvoiceService from '../../services/invoice.service';
import { requestService } from '../../services/requestService';
import DataTable from '../../components/common/DataTable';
import { usePagination } from '../../hooks/usePagination';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';
import { getBillToFromInvoice } from '../../utils/invoiceBillTo';
import api from '../../api/axios';
import RequestInsuranceServiceSummary from '../../components/request/RequestInsuranceServiceSummary';

const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('invoiceDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [imageErrors, setImageErrors] = useState({ before: false, after: false });
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState<string>(searchParams.get('dateTo') || '');

  // Helper function to get image URL from photo data
  const getImageUrl = (photoData: any): string | null => {
    if (!photoData) return null;
    
    let url = photoData?.data?.attributes?.url || 
              photoData?.url || 
              photoData?.data?.url || 
              photoData?.attributes?.url || 
              '';
    
    // Check if URL is valid (not empty, not null, not undefined)
    if (!url || typeof url !== 'string' || url.trim() === '' || url === 'null' || url === 'undefined') {
      return null;
    }
    
    // If URL is relative (starts with /), prepend the API base URL
    // Strapi uploads are served from the root, not from /api
    if (url.startsWith('/')) {
      const API_URL = process.env.REACT_APP_API_URL || 'https://darb.aiastra.io';
      url = `${API_URL}${url}`;
    }
    
    return url;
  };

  // Helper function to check if image data exists and has valid URL
  const hasValidImage = (photoData: any): boolean => {
    if (!photoData) return false;
    const url = getImageUrl(photoData);
    return url !== null && url !== '';
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params: any = {
        'populate[customer]': '*',
        'populate[insuranceClient]': '*',
        'populate[request]': '*',
        'pagination[page]': pagination.page + 1, // Strapi uses 1-based indexing
        'pagination[pageSize]': pagination.pageSize,
        sort: `${sortField}:${sortOrder}`,
      };

      // Add search filter if search term exists
      if (searchTerm) {
        params['filters[$or][0][invoiceNumber][$containsi]'] = searchTerm;
        params['filters[$or][1][customer][name][$containsi]'] = searchTerm;
        params['filters[$or][2][customer][companyName][$containsi]'] = searchTerm;
        params['filters[$or][3][insuranceClient][customerName][$containsi]'] = searchTerm;
        params['filters[$or][4][insuranceClient][companyName][$containsi]'] = searchTerm;
        params['filters[$or][5][insuranceClient][vinNumber][$containsi]'] = searchTerm;
      }

      // Add date filters
      if (dateFrom) {
        params['filters[createdAt][$gte]'] = new Date(dateFrom).toISOString();
      }
      if (dateTo) {
        // End of day for the selected date
        const endDate = new Date(dateTo);
        endDate.setHours(23, 59, 59, 999);
        params['filters[createdAt][$lte]'] = endDate.toISOString();
      }

      // Add invoice type filter
      if (invoiceTypeFilter && invoiceTypeFilter !== 'all') {
        params['filters[invoiceType][$eq]'] = invoiceTypeFilter;
      }

      console.log('🔍 InvoiceList: Fetching with params:', {
        dateFrom,
        dateTo,
        dateFromISO: dateFrom ? new Date(dateFrom).toISOString() : null,
        dateToISO: dateTo ? new Date(dateTo + 'T23:59:59.999Z').toISOString() : null,
      });

      const response = await InvoiceService.getAll(params);
      const invoicesData = response.data?.data || [];
      const paginationMeta = response.data?.meta?.pagination || {};
      
      console.log('📊 InvoiceList: Received', invoicesData.length, 'invoices, total:', paginationMeta.total);
      if (invoicesData.length > 0) {
        console.log('Sample invoice createdAt:', invoicesData[0]?.attributes?.createdAt);
      }
      
      setInvoices(invoicesData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Search is now handled server-side
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Reset to first page when search changes
    setPagination({ page: 0 });
  };

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch invoices when pagination, sort, invoice type, or date filters change
  useEffect(() => {
    fetchInvoices();
  }, [pagination.page, pagination.pageSize, sortField, sortOrder, invoiceTypeFilter, dateFrom, dateTo]);

  const handleViewRequest = async (requestId: number | string) => {
    if (requestId && requestId !== '0') {
      try {
        setError('');
        const response = await requestService.getById(Number(requestId));
        setViewingRequest(response.data);
        setImageErrors({ before: false, after: false });
        setRequestModalOpen(true);
      } catch (err) {
        console.error('Error loading request:', err);
        setError('Failed to load request details.');
      }
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handleDownloadPDF = async (invoiceId: number, invoiceType: 'customer' | 'insurance' = 'customer') => {
    try {
      setError('');
      // Fetch invoice with full details (customer, insuranceClient, serviceProvider for bill-to)
      const invoiceResponse = await InvoiceService.getOne(invoiceId, {
        populate: ['customer', 'insuranceClient', 'insuranceClient.insuranceCompany', 'serviceProvider', 'request'],
      });
      const invoice = invoiceResponse.data?.data;
      if (!invoice) {
        setError('Invoice not found.');
        return;
      }

      // Fetch line items
      const itemsResponse = await InvoiceService.getItems(invoiceId);
      const lineItems = itemsResponse.data?.data || [];

      const doc = new jsPDF();
      const data = invoice.attributes || invoice;
      const billTo = getBillToFromInvoice(invoice);

      // Load and add logo
      await addLogoToPDF(doc);

      // Add company name
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

      // Add invoice type header
      if (invoiceType === 'insurance') {
        doc.setFontSize(14);
        doc.setTextColor(33, 150, 243);
        doc.text('INSURANCE INVOICE', 105, 20, { align: 'center' });
      }

      // Add invoice details on the right
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`DATE: ${formatDate(data.invoiceDate)}`, 175, invoiceType === 'insurance' ? 30 : 20, { align: 'right' });
      doc.text(`INVOICE #: ${data.invoiceNumber}`, 175, invoiceType === 'insurance' ? 37 : 27, { align: 'right' });
      doc.text(`Status: ${(data.status || 'draft').toUpperCase()}`, 175, invoiceType === 'insurance' ? 44 : 34, { align: 'right' });

      // Add company details
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let companyY = invoiceType === 'insurance' ? 58 : 48;
      doc.text('CR: 1366462', 20, companyY);
      companyY += 5;
      doc.text('BLOCK 255, STREET, WAY 5508, BUILDING 564', 20, companyY);
      companyY += 5;
      doc.text('VAT NUM: OM1100429932', 20, companyY);
      companyY += 5;
      if (data.omanTaxId) {
        doc.text(`Oman Tax ID: ${data.omanTaxId}`, 20, companyY);
        companyY += 5;
      }
      doc.text('Phone: 80081818 - 24790888', 20, companyY);
      companyY += 5;
      doc.text('Email: INFO@DARBNS.COM', 20, companyY);

      // Add bill-to information (from insuranceClient, customer, serviceProvider, or customerName)
      let billToY = (invoiceType === 'insurance' ? 90 : 75) + (data.omanTaxId ? 5 : 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('BILL TO:', 20, billToY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      let yPos = billToY + 7;
      doc.text(billTo?.name || '-', 20, yPos);

      if (invoiceType === 'insurance' && billTo?.insuranceCompany) {
        yPos += 5;
        doc.setFont('helvetica', 'bold');
        doc.text(`Insurance Company: ${billTo.insuranceCompany}`, 20, yPos);
        doc.setFont('helvetica', 'normal');
      }
      if (invoiceType === 'insurance' && billTo?.policyNumber) {
        yPos += 5;
        doc.text(`Policy Number: ${billTo.policyNumber}`, 20, yPos);
      }
      if (invoiceType === 'insurance' && billTo?.vinNumber) {
        yPos += 5;
        doc.text(`VIN Number: ${billTo.vinNumber}`, 20, yPos);
      }
      if (billTo?.email) {
        yPos += 5;
        doc.text(`Email: ${billTo.email}`, 20, yPos);
      }
      if (billTo?.phone) {
        yPos += 5;
        doc.text(`Phone: ${billTo.phone}`, 20, yPos);
      }
      if (invoiceType === 'customer' && billTo?.vinNumber) {
        yPos += 5;
        doc.text(`VIN: ${billTo.vinNumber}`, 20, yPos);
      }
      if (billTo?.address) {
        yPos += 5;
        doc.text(`Address: ${billTo.address}`, 20, yPos);
      }

      // Add line items table
      const tableData = lineItems.map((item: any) => {
        const itemAttrs = item.attributes || item;
        const product = itemAttrs?.product?.data?.attributes || itemAttrs?.product;
        const description = product?.name || itemAttrs?.description || '-';
        const unitPrice = itemAttrs?.unitPrice || 0;
        
        if (invoiceType === 'insurance') {
          return [
            description,
            '-',
            `OMR ${unitPrice.toFixed(2)}`,
            `OMR ${(itemAttrs?.total || 0).toFixed(2)}`,
          ];
        } else {
          return [
            description,
            itemAttrs?.quantity || 0,
            `OMR ${unitPrice.toFixed(2)}`,
            `OMR ${(itemAttrs?.discount || 0).toFixed(2)}`,
            `OMR ${(itemAttrs?.tax || 0).toFixed(2)}`,
            `OMR ${(itemAttrs?.total || 0).toFixed(2)}`,
          ];
        }
      });

      const tableHeaders = invoiceType === 'insurance' 
        ? [['DESCRIPTION', 'SERVICE', 'RATE', 'AMOUNT']]
        : [['Product', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total']];

      autoTable(doc, {
        startY: yPos + 10,
        head: tableHeaders,
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [33, 150, 243],
          textColor: 255,
          fontStyle: 'bold',
        },
        styles: {
          fontSize: 9,
        },
        columnStyles: invoiceType === 'insurance' 
          ? {
              0: { cellWidth: 60 },
              1: { cellWidth: 40 },
              2: { halign: 'right', cellWidth: 30 },
              3: { halign: 'right', cellWidth: 30 },
            }
          : {
              0: { cellWidth: 60 },
              1: { halign: 'right', cellWidth: 20 },
              2: { halign: 'right', cellWidth: 25 },
              3: { halign: 'right', cellWidth: 25 },
              4: { halign: 'right', cellWidth: 25 },
              5: { halign: 'right', cellWidth: 25 },
            },
      });

      // Add totals
      const finalY = (doc as any).lastAutoTable.finalY || yPos + 10;
      doc.setFontSize(10);
      
      doc.text('Subtotal:', 130, finalY + 10);
      doc.text(`OMR ${(data.subtotal || 0).toFixed(2)}`, 180, finalY + 10, { align: 'right' });
      
      if (invoiceType === 'customer') {
        doc.text('Discount:', 130, finalY + 17);
        doc.text(`- OMR ${(data.discount || 0).toFixed(2)}`, 180, finalY + 17, { align: 'right' });
      }
      
      doc.text('Tax:', 130, finalY + (invoiceType === 'customer' ? 24 : 17));
      doc.text(`OMR ${(data.tax || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 24 : 17), { align: 'right' });
      
      // Total line
      doc.setDrawColor(33, 150, 243);
      doc.setLineWidth(0.5);
      doc.line(130, finalY + (invoiceType === 'customer' ? 27 : 20), 180, finalY + (invoiceType === 'customer' ? 27 : 20));
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', 130, finalY + (invoiceType === 'customer' ? 34 : 27));
      doc.text(`OMR ${(data.total || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 34 : 27), { align: 'right' });

      // Payment info
      if (data.paidAmount > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Paid Amount:', 130, finalY + (invoiceType === 'customer' ? 44 : 37));
        doc.text(`OMR ${(data.paidAmount || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 44 : 37), { align: 'right' });
        
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 0, 0);
        doc.text('Balance Due:', 130, finalY + (invoiceType === 'customer' ? 51 : 44));
        doc.text(`OMR ${(data.balanceDue || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 51 : 44), { align: 'right' });
        doc.setTextColor(0, 0, 0);
      }

      // Add notes if any
      if (data.notes) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 20, finalY + (invoiceType === 'customer' ? 65 : 58));
        doc.setFont('helvetica', 'normal');
        const splitNotes = doc.splitTextToSize(data.notes, 170);
        doc.text(splitNotes, 20, finalY + (invoiceType === 'customer' ? 72 : 65));
      }

      // Save the PDF
      const fileName = invoiceType === 'insurance' 
        ? `Insurance-Invoice-${data.invoiceNumber}.pdf`
        : `Customer-Invoice-${data.invoiceNumber}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('Error downloading invoice PDF:', err);
      setError('Failed to download invoice PDF.');
    }
  };

  const columns = [
    { id: 'invoiceNumber', label: 'Invoice #' },
    {
      id: 'request',
      label: 'Request ID',
      renderCell: ({ row, value }: any) => {
        // DataTable normalizes row to { ...row.attributes, id: row.id }
        // The request relation should be accessible from row.request or value
        // Match the pattern from Reports.tsx: attrs.request?.data?.attributes || attrs.request?.data || attrs.request
        let request = value || row.request;
        
        // If not found, try accessing from the original structure
        if (!request) {
          request = row.attributes?.request || row.request;
        }
        
        // Handle Strapi relation format - same as Reports.tsx
        // Format: { data: { id, attributes: { requestNumber, ... } } } or { id, attributes: {...} } or { id, requestNumber, ... }
        const requestData = request?.data?.attributes || request?.data || request?.attributes || request;
        const requestId = requestData?.id || request?.data?.id || request?.id || null;
        const requestNumber = requestData?.requestNumber || requestData?.attributes?.requestNumber || request?.requestNumber || (requestId ? `REQ-${requestId}` : '-');
        
        // Only make clickable if we have a valid request ID (must be a number)
        if (requestId && requestId !== '0' && requestId !== null && requestId !== undefined) {
          return (
            <Typography
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                handleViewRequest(Number(requestId));
              }}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': {
                  color: 'primary.dark',
                },
              }}
            >
              {requestNumber}
            </Typography>
          );
        }
        return <span>{requestNumber}</span>;
      },
    },
    {
      id: 'customer',
      label: 'Customer Name',
      renderCell: ({ row }: any) => {
        // Handle Strapi format - check both row.attributes and row directly
        const invoiceData = row.attributes || row;
        const customer = invoiceData.customer?.data?.attributes || row.customer?.data?.attributes;
        const insuranceClient = invoiceData.insuranceClient?.data?.attributes || 
                                row.insuranceClient?.data?.attributes;
        // Prioritize customer name, then insurance client name
        return customer?.name || customer?.firstName || 
               insuranceClient?.customerName || insuranceClient?.companyName || 
               insuranceClient?.contactPerson || '-';
      },
    },
    {
      id: 'invoiceType',
      label: 'Invoice Type',
      renderCell: ({ value }: any) => {
        if (!value) return '-';
        const label = String(value)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        return label;
      },
    },
    {
      id: 'invoiceDate',
      label: 'Date',
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'total',
      label: 'Total',
      align: 'right' as const,
      format: (value: number) => `OMR ${value?.toFixed(2) || '0.00'}`,
    },
    {
      id: 'paidAmount',
      label: 'Paid',
      align: 'right' as const,
      format: (value: number) => `OMR ${value?.toFixed(2) || '0.00'}`,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right' as const,
      renderCell: ({ row }: any) => {
        const invoiceId = row.id || row.attributes?.id;
        const invoiceData = row.attributes || row;
        const invoiceType = invoiceData.invoiceType;
        // Determine if it's an insurance invoice (has insurance client and invoiceType is insurance_company_monthly)
        const isInsuranceInvoice = invoiceData.invoiceType === 'insurance_company_monthly' || 
                                  (invoiceData.insuranceClient && !invoiceData.customer);
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <Tooltip title="Download Customer Invoice">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (invoiceId) handleDownloadPDF(invoiceId, 'customer');
                }}
                color="primary"
              >
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isInsuranceInvoice && (
              <Tooltip title="Download Insurance Invoice">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (invoiceId) handleDownloadPDF(invoiceId, 'insurance');
                  }}
                  color="secondary"
                >
                  <PdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Invoices</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/invoices/create')}
        >
          Create Invoice
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
        />
        <TextField
          type="date"
          label="From Date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          type="date"
          label="To Date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Invoice Type</InputLabel>
          <Select
            value={invoiceTypeFilter}
            label="Invoice Type"
            onChange={(e) => {
              setInvoiceTypeFilter(e.target.value);
              setPagination({ page: 0 });
            }}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="insurance">Insurance</MenuItem>
            <MenuItem value="service_provider">Service Provider</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="insurance_customer">Insurance Customer</MenuItem>
            <MenuItem value="insurance_company_monthly">Insurance Company Monthly</MenuItem>
            <MenuItem value="service_provider_monthly">Service Provider Monthly</MenuItem>
            <MenuItem value="service_provider_to_darb">Service Provider to DARB</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={`${sortField}:${sortOrder}`}
            label="Sort By"
            onChange={(e) => {
              const [field, order] = e.target.value.split(':');
              setSortField(field);
              setSortOrder(order as 'asc' | 'desc');
              setPagination({ page: 0 });
            }}
          >
            <MenuItem value="invoiceDate:desc">Date (Newest First)</MenuItem>
            <MenuItem value="invoiceDate:asc">Date (Oldest First)</MenuItem>
            <MenuItem value="invoiceNumber:asc">Invoice # (A-Z)</MenuItem>
            <MenuItem value="invoiceNumber:desc">Invoice # (Z-A)</MenuItem>
            <MenuItem value="total:desc">Total (High to Low)</MenuItem>
            <MenuItem value="total:asc">Total (Low to High)</MenuItem>
            <MenuItem value="dueDate:asc">Due Date (Earliest)</MenuItem>
            <MenuItem value="dueDate:desc">Due Date (Latest)</MenuItem>
            <MenuItem value="status:asc">Status (A-Z)</MenuItem>
            <MenuItem value="status:desc">Status (Z-A)</MenuItem>
          </Select>
        </FormControl>
        {(dateFrom || dateTo || invoiceTypeFilter !== 'all') && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setDateFrom('');
              setDateTo('');
              setInvoiceTypeFilter('all');
              setPagination({ page: 0 });
            }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <DataTable
        columns={columns}
        data={invoices}
        loading={loading}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalCount={pagination.total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        onView={(row) => {
          const invoiceId = row.id || row.attributes?.id;
          if (invoiceId) navigate(`/invoices/${invoiceId}`);
        }}
        onEdit={(row) => {
          const invoiceId = row.id || row.attributes?.id;
          if (invoiceId) navigate(`/invoices/${invoiceId}/edit`);
        }}
      />

      {/* Request Details Modal */}
      <Dialog open={requestModalOpen} onClose={() => setRequestModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Request Details</Typography>
            {viewingRequest && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={viewingRequest.requestNumber || viewingRequest.attributes?.requestNumber || `REQ-${viewingRequest.id}`}
                  color="primary"
                  size="small"
                />
                {(viewingRequest.imported || viewingRequest.attributes?.imported) && (
                  <Chip
                    label="Imported"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingRequest && (() => {
            const attrs = viewingRequest.attributes || viewingRequest;
            const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient?.data || attrs.insuranceClient;
            const customer = attrs.customer?.data?.attributes || attrs.customer?.data || attrs.customer;
            const serviceProvider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider?.data || attrs.serviceProvider;
            
            return (
              <Stack spacing={3} sx={{ mt: 1 }}>
                <RequestInsuranceServiceSummary
                  request={viewingRequest}
                  onClientClick={(clientId) => {
                    setRequestModalOpen(false);
                    navigate(`/insurance-clients/${clientId}/overview`);
                  }}
                />

                {/* Request Information */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    Request Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Request Number
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {attrs.requestNumber || `REQ-${viewingRequest.id}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={(attrs.status || 'pending').replace('_', ' ')}
                          color={
                            attrs.status === 'completed' ? 'success' :
                            attrs.status === 'cancelled' ? 'error' :
                            attrs.status === 'pending' ? 'warning' : 'info'
                          }
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {attrs.description || 'No description'}
                      </Typography>
                    </Box>
                    {attrs.serviceType && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Service Type
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {attrs.serviceType}
                        </Typography>
                      </Box>
                    )}
                    {serviceProvider && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Service Provider
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {serviceProvider.name || 'N/A'}
                        </Typography>
                      </Box>
                    )}
                    {attrs.phoneNumber && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Phone Number
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {attrs.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                    {attrs.scheduledDate && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Scheduled Date
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {new Date(attrs.scheduledDate).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    {attrs.completedDate && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Completed Date
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {new Date(attrs.completedDate).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Client Information */}
                {(insuranceClient || customer) && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                      Client Information
                    </Typography>
                    {insuranceClient ? (
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            Client Name
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {insuranceClient.customerName || insuranceClient.companyName || insuranceClient.contactPerson || 'N/A'}
                          </Typography>
                        </Box>
                        {insuranceClient.vinNumber && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              VIN Number
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {insuranceClient.vinNumber}
                            </Typography>
                          </Box>
                        )}
                        {insuranceClient.phoneNumber && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Phone Number
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {insuranceClient.phoneNumber}
                            </Typography>
                          </Box>
                        )}
                        {insuranceClient.email && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Email
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {insuranceClient.email}
                            </Typography>
                          </Box>
                        )}
                        {(insuranceClient.carMake || insuranceClient.carModel) && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Vehicle
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {insuranceClient.carMake || ''} {insuranceClient.carModel || ''} {insuranceClient.carYear ? `(${insuranceClient.carYear})` : ''} {insuranceClient.plateNumber ? `- ${insuranceClient.plateNumber}` : ''}
                            </Typography>
                          </Box>
                        )}
                        {insuranceClient.insuranceCompany && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Insurance Company
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {insuranceClient.insuranceCompany?.data?.attributes?.name || insuranceClient.insuranceCompany?.attributes?.name || insuranceClient.insuranceCompany?.name || 'N/A'}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    ) : customer ? (
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            Customer Name
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {customer.name || customer.companyName || customer.contactPerson || customer.firstName ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : 'N/A'}
                          </Typography>
                        </Box>
                        {customer.phone && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Phone Number
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {customer.phone}
                            </Typography>
                          </Box>
                        )}
                        {customer.email && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Email
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {customer.email}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    ) : null}
                  </Box>
                )}

                {/* Evidence Photos */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    Evidence Photos
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Before
                      </Typography>
                      {(() => {
                        const beforePhoto = attrs.evidencePhotoBefore?.data || attrs.evidencePhotoBefore;
                        const imageUrl = getImageUrl(beforePhoto);
                        const hasImage = hasValidImage(beforePhoto) && !imageErrors.before && imageUrl;
                        
                        return hasImage ? (
                          <Box>
                            <img
                              src={imageUrl}
                              alt="Before"
                              style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                              onError={() => setImageErrors(prev => ({ ...prev, before: true }))}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: '300px',
                              height: '300px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              backgroundColor: '#f5f5f5',
                              color: 'text.secondary'
                            }}
                          >
                            <Typography variant="body2">No image</Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        After
                      </Typography>
                      {(() => {
                        const afterPhoto = attrs.evidencePhotoAfter?.data || attrs.evidencePhotoAfter;
                        const imageUrl = getImageUrl(afterPhoto);
                        const hasImage = hasValidImage(afterPhoto) && !imageErrors.after && imageUrl;
                        
                        return hasImage ? (
                          <Box>
                            <img
                              src={imageUrl}
                              alt="After"
                              style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                              onError={() => setImageErrors(prev => ({ ...prev, after: true }))}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: '300px',
                              height: '300px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              backgroundColor: '#f5f5f5',
                              color: 'text.secondary'
                            }}
                          >
                            <Typography variant="body2">No image</Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;

